import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type Rect = { top: number; left: number; width: number; height: number };

const ZOOM_PADDING = 24;
const ZOOM_DURATION_MS = 200;

const computeTargetRect = (naturalWidth: number, naturalHeight: number): Rect => {
  const aw = window.innerWidth - ZOOM_PADDING * 2;
  const ah = window.innerHeight - ZOOM_PADDING * 2;
  const aspect = naturalWidth / naturalHeight;
  let w: number;
  let h: number;
  if (aw / ah > aspect) {
    h = ah;
    w = h * aspect;
  } else {
    w = aw;
    h = w / aspect;
  }
  return {
    top: (window.innerHeight - h) / 2,
    left: (window.innerWidth - w) / 2,
    width: w,
    height: h,
  };
};

type GetImageElement = (index: number) => HTMLImageElement | null;

type ArticleImageGalleryContextValue = {
  register: (id: string, images: string[], getElement: GetImageElement) => void;
  unregister: (id: string) => void;
  open: (id: string, index: number) => void;
};

const ArticleImageGalleryContext = createContext<ArticleImageGalleryContextValue | null>(
  null
);

export const useArticleImageGallery = () => useContext(ArticleImageGalleryContext);

type Layout = {
  id: string;
  images: string[];
  getElement: GetImageElement;
};

type ZoomState = {
  layoutId: string;
  index: number;
  origin: Rect;
  target: Rect;
};

export const ArticleImageGalleryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const layoutsRef = useRef<Layout[]>([]);
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const register = useCallback(
    (id: string, images: string[], getElement: GetImageElement) => {
      const next = { id, images, getElement };
      const idx = layoutsRef.current.findIndex((l) => l.id === id);
      if (idx >= 0) {
        layoutsRef.current[idx] = next;
      } else {
        layoutsRef.current.push(next);
      }
    },
    []
  );

  const unregister = useCallback((id: string) => {
    layoutsRef.current = layoutsRef.current.filter((l) => l.id !== id);
  }, []);

  const measureRects = (layoutId: string, index: number) => {
    const layout = layoutsRef.current.find((l) => l.id === layoutId);
    if (!layout) return null;
    const imgEl = layout.getElement(index);
    if (!imgEl) return null;
    const r = imgEl.getBoundingClientRect();
    const origin: Rect = { top: r.top, left: r.left, width: r.width, height: r.height };
    const nw = imgEl.naturalWidth || r.width;
    const nh = imgEl.naturalHeight || r.height;
    const target = computeTargetRect(nw, nh);
    return { origin, target };
  };

  const open = useCallback((id: string, index: number) => {
    const rects = measureRects(id, index);
    if (!rects) return;
    setZoom({ layoutId: id, index, ...rects });
    setIsOpen(false);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setIsOpen(true))
    );
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    window.setTimeout(() => setZoom(null), ZOOM_DURATION_MS);
  }, []);

  const flattenInDomOrder = (): { layoutId: string; index: number }[] => {
    const layouts = layoutsRef.current.filter((l) => l.getElement(0));
    layouts.sort((a, b) => {
      const ea = a.getElement(0);
      const eb = b.getElement(0);
      if (!ea || !eb) return 0;
      const pos = ea.compareDocumentPosition(eb);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    const flat: { layoutId: string; index: number }[] = [];
    for (const layout of layouts) {
      for (let i = 0; i < layout.images.length; i++) {
        flat.push({ layoutId: layout.id, index: i });
      }
    }
    return flat;
  };

  const navigate = (dir: 1 | -1) => {
    if (!zoom) return;
    const flat = flattenInDomOrder();
    const pos = flat.findIndex(
      (f) => f.layoutId === zoom.layoutId && f.index === zoom.index
    );
    if (pos === -1) return;
    const nextPos = pos + dir;
    if (nextPos < 0 || nextPos >= flat.length) return;
    const next = flat[nextPos];
    const rects = measureRects(next.layoutId, next.index);
    if (!rects) return;
    setZoom({ layoutId: next.layoutId, index: next.index, ...rects });
  };

  useEffect(() => {
    if (!zoom) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') navigate(1);
      else if (e.key === 'ArrowLeft') navigate(-1);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [zoom, close]);

  const collapsedTransform = useMemo(() => {
    if (!zoom) return '';
    const { origin, target } = zoom;
    const sx = origin.width / target.width;
    const sy = origin.height / target.height;
    const tx = origin.left + origin.width / 2 - (target.left + target.width / 2);
    const ty = origin.top + origin.height / 2 - (target.top + target.height / 2);
    return `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
  }, [zoom]);

  const currentImage = useMemo(() => {
    if (!zoom) return null;
    const layout = layoutsRef.current.find((l) => l.id === zoom.layoutId);
    return layout?.images[zoom.index] ?? null;
  }, [zoom]);

  const value = useMemo<ArticleImageGalleryContextValue>(
    () => ({ register, unregister, open }),
    [register, unregister, open]
  );

  return (
    <ArticleImageGalleryContext.Provider value={value}>
      {children}
      {zoom && currentImage && (
        <div
          className={`article-image-zoom-overlay${isOpen ? ' is-open' : ''}`}
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <img
            key={`${zoom.layoutId}-${zoom.index}`}
            src={currentImage}
            alt="Zoomed"
            className="article-image-zoom-img"
            style={{
              top: `${zoom.target.top}px`,
              left: `${zoom.target.left}px`,
              width: `${zoom.target.width}px`,
              height: `${zoom.target.height}px`,
              transform: isOpen ? 'translate(0, 0) scale(1, 1)' : collapsedTransform,
            }}
          />
        </div>
      )}
    </ArticleImageGalleryContext.Provider>
  );
};
